import { sessionQuery, useApi } from '@classgo/app-kit';
import { Badge, Button, Card, EmptyState, Modal, Table, useToast } from '@classgo/ui';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { staffApi, staffQueries } from '../../api/staff-api';
import type { StaffUserView } from '../../api/types';
import { PersonForm } from '../../forms/person-form';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/format';

/**
 * The ClassGO team: who can use this console. Super admins may add other super admins.
 */
export const TeamScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery(api));
  const { data: people } = useSuspenseQuery(staffQueries.staffUsers(api));
  const [adding, setAdding] = useState(false);
  const canCreateSuperAdmin = session.kind === 'staff' && session.staff.role === 'SUPER_ADMIN';
  const addButton = <Button onClick={() => setAdding(true)}>{t('team.add')}</Button>;
  const roleLabel = (role: string) =>
    role === 'SUPER_ADMIN' ? t('team.role.SUPER_ADMIN') : t('team.role.STAFF_ADMIN');
  return (
    <>
      <Card title={t('team.title')} actions={addButton} flush>
        <Table<StaffUserView>
          caption={t('team.caption')}
          rows={people}
          rowKey={(row) => row.id}
          empty={<EmptyState title={t('team.empty')} action={addButton} />}
          columns={[
            {
              key: 'person',
              header: t('team.column.person'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">
                    {[row.givenName, row.familyName].filter(Boolean).join(' ') || row.email}
                  </p>
                  <p className="text-xs text-gray-500">{row.email}</p>
                </div>
              ),
            },
            { key: 'role', header: t('team.column.role'), cell: (row) => roleLabel(row.role) },
            {
              key: 'status',
              header: t('team.column.status'),
              cell: (row) => (
                <Badge tone={row.status === 'active' ? 'success' : 'gray'}>{row.status}</Badge>
              ),
            },
            {
              key: 'created',
              header: t('team.column.created'),
              cell: (row) => formatDate(row.createdAt),
            },
            ...(canCreateSuperAdmin
              ? [
                  {
                    key: 'mfa',
                    header: t('team.column.mfa'),
                    cell: (row: StaffUserView) => (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          await staffApi(api).resetStaffMfa(row.id);
                          toast.show({
                            tone: 'success',
                            title: t('team.mfaReset', { email: row.email }),
                          });
                        }}
                      >
                        {t('team.resetMfa')}
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <Modal open={adding} onClose={() => setAdding(false)} title={t('team.add')}>
        <PersonForm
          kind="staff"
          canCreateSuperAdmin={canCreateSuperAdmin}
          submitLabel={t('team.add')}
          onSubmit={async (values) => {
            await staffApi(api).createStaffUser({
              email: values.email.trim().toLowerCase(),
              role: values.role,
              ...(values.givenName.trim() ? { givenName: values.givenName.trim() } : {}),
              ...(values.familyName.trim() ? { familyName: values.familyName.trim() } : {}),
            });
            await queryClient.invalidateQueries({ queryKey: ['staff', 'users'] });
            setAdding(false);
            toast.show({ tone: 'success', title: t('team.created') });
          }}
        />
      </Modal>
    </>
  );
};
